import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router";
import {
    Plus, Search, Edit2, Loader2, Link as LinkIcon, Trash2, X, RefreshCw,
} from "lucide-react";
import * as Icons from "lucide-react";
import { servicesService, type Service } from "../../../lib/services/servicesService";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export function AdminServices() {
    const navigate = useNavigate();
    const [services, setServices] = useState<Service[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);

    const loadData = () => {
        setLoading(true);
        servicesService.list()
            .then((data) => setServices(data.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))))
            .catch(console.error)
            .finally(() => setLoading(false));
    };
    useEffect(loadData, []);

    const confirmDelete = (id: string) => {
        setDeleteId(id);
        setIsDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await servicesService.remove(deleteId);
            setServices((s) => s.filter((x) => x.$id !== deleteId));
            setIsDeleteOpen(false);
            setDeleteId(null);
        } catch (e) {
            console.error(e);
            alert("Failed to delete service.");
        }
    };

    const filtered = services.filter((s) =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.shortTitle.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-4">
            {/* ── Header row ── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border">
                <div className="flex items-center justify-between w-full sm:w-auto">
                    <div className="flex items-center gap-3 shrink-0">
                        <h2 className="text-base font-semibold text-foreground">Services</h2>
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{services.length}</span>
                    </div>
                    {/* Mobile actions */}
                    <div className="sm:hidden flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={loadData} className="h-8 w-8 p-0 text-muted-foreground hover:text-white">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        </Button>
                        <Button onClick={() => navigate("/admin/services/new")} size="sm" className="h-8 gap-1.5 px-3">
                            <Plus className="w-3.5 h-3.5" /> <span className="hidden xs:inline">Add</span>
                        </Button>
                    </div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-[260px] max-w-full">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                        <Input
                            placeholder="Search services…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-8 h-8 text-sm bg-background shadow-none focus-visible:ring-1 border border-border w-full"
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Desktop actions */}
                    <div className="hidden sm:flex items-center gap-2 shrink-0">
                        <Button variant="ghost" size="sm" onClick={loadData} className="h-8 text-muted-foreground hover:text-white gap-1.5">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </Button>
                        <Button onClick={() => navigate("/admin/services/new")} size="sm" className="h-8 gap-1.5">
                            <Plus className="w-3.5 h-3.5" /> Add Service
                        </Button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 bg-card rounded border border-border min-h-[40vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                    <p className="text-sm font-medium text-muted-foreground">Loading services...</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20 bg-card border border-dashed rounded border-border">
                    <p className="text-muted-foreground mb-3">No services found.</p>
                    <Button variant="link" onClick={() => navigate("/admin/services/new")} className="text-primary">Create your first service →</Button>
                </div>
            ) : (
                <div className="grid lg:grid-cols-2 gap-5">
                    {filtered.map((service) => {
                        const Icon = (Icons as any)[service.icon] || Icons.Settings;
                        const fCount = (() => { try { return JSON.parse(service.features).length; } catch { return 0; } })();
                        const bCount = (() => { try { return JSON.parse(service.benefits).length; } catch { return 0; } })();
                        const qCount = (() => { try { return JSON.parse(service.faqs).length; } catch { return 0; } })();

                        return (
                            <Card key={service.$id} className="overflow-hidden shadow-none flex flex-col group">
                                <CardContent className="p-0 flex flex-col h-full">
                                    <div className="p-6 flex gap-5 items-start">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                                            style={{ backgroundColor: `${service.colorAccent || '#1a2d50'}20`, color: service.colorAccent || '#1a2d50' }}
                                        >
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between mb-1">
                                                <h3 className="font-bold text-foreground text-lg truncate pr-4">{service.title}</h3>
                                                <Badge variant="outline" className={`shrink-0 ${service.status === "Active" ? "bg-emerald-50 text-emerald-700 border-transparent" : "bg-muted text-muted-foreground border-transparent"}`}>
                                                    {service.status}
                                                </Badge>
                                            </div>
                                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3" style={{ lineHeight: 1.6 }}>{service.desc}</p>
                                            <div className="grid grid-cols-3 gap-2 py-3 border-y border-border/50 mt-2">
                                                <div className="text-center"><div className="text-lg font-bold text-foreground">{fCount}</div><div className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Features</div></div>
                                                <div className="text-center border-l border-border/50"><div className="text-lg font-bold text-foreground">{bCount}</div><div className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Benefits</div></div>
                                                <div className="text-center border-l border-border/50"><div className="text-lg font-bold text-foreground">{qCount}</div><div className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">FAQs</div></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-muted/30 px-6 py-3 border-t border-border mt-auto flex items-center justify-between">
                                        <Link to={`/services/${service.slug}`} target="_blank" className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1.5 transition-colors">
                                            <LinkIcon className="w-3.5 h-3.5" /> View Live
                                        </Link>
                                        <div className="flex items-center gap-2">
                                            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/services/${service.$id}`)} className="h-8 text-xs font-semibold shadow-none">
                                                <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => confirmDelete(service.$id!)} className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* DELETE CONFIRMATION */}
            <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <AlertDialogContent className="shadow-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this service?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. The service will be permanently removed from the website.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
                            Delete Service
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
